import { useCallback, useEffect, useState } from 'react'
import { validPublicStoreStatus, type PublicStoreStatus } from './publicStoreStatus'

export type PublicStoreStatusState =
  | { state: 'checking'; status: null }
  | { state: 'unavailable'; status: null }
  | { state: 'open'; status: PublicStoreStatus }
  | { state: 'closed'; status: PublicStoreStatus }

export function usePublicStoreStatus(): PublicStoreStatusState {
  const [status, setStatus] = useState<PublicStoreStatus | null>(null)
  const [unavailable, setUnavailable] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/store/status', { credentials: 'same-origin', cache: 'no-store' })
      if (!response.ok) throw new Error('store status unavailable')
      const payload = await response.json() as unknown
      if (!validPublicStoreStatus(payload)) throw new Error('invalid store status')
      setStatus(payload)
      setUnavailable(false)
    } catch {
      setStatus(null)
      setUnavailable(true)
    }
  }, [])

  useEffect(() => {
    void refresh()
    const timer = window.setInterval(() => void refresh(), 5_000)
    const onFocus = () => void refresh()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [refresh])

  if (!status) return { state: unavailable ? 'unavailable' : 'checking', status: null }
  return { state: status.ordersOpen ? 'open' : 'closed', status }
}
